import { useTranslation } from 'react-i18next'

import { useCsvExport } from '@features/exports/hooks/useCsvExport'
import { useXlsxExport } from '@features/exports/hooks/useXlsxExport'
import { VatDeclarationsPeriod } from '@features/vatDeclarations/types/vatDeclarationsPeriod'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../../../../../enums/notificationKeys'
import { getCurrentLocale } from '../../../../../../../utils'
import { useFetchVatDeclarationAuditTrail } from '../../../../../hooks/useFetchVatDeclarationAuditTrail'
import { getPeriodData } from '../../../../../utils/getPeriodData'
import { buildFlatAuditTrailTable } from '../elements/exports/xlsx/utils/buildFlatAuditTrailTable'
import { buildFlatReportHeader } from '../elements/exports/xlsx/utils/buildFlatReportHeader'
import { buildFlatReportTable } from '../elements/exports/xlsx/utils/buildFlatReportTable'

interface UseSpreadsheetExportProps {
  vatDeclaration: VatDeclarationsPeriod | undefined
  withAuditTrail: boolean
}

export const useSpreadsheetExport = ({ vatDeclaration, withAuditTrail }: UseSpreadsheetExportProps) => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const periodData = getPeriodData(vatDeclaration?.period || '')
  const periodText = vatDeclaration?.periodText || ''

  const { data: auditTrailData } = useFetchVatDeclarationAuditTrail({
    showVoided: true,
    vatDeclarationId: vatDeclaration?.id,
  })

  const { name: organizationName, countryId, localeId } = organization || {}

  const reportHeader = buildFlatReportHeader({
    locale: localeId,
    name: organizationName || '',
    periodText,
    t,
  })
  const reportData = buildFlatReportTable({ data: vatDeclaration?.report, countryId: countryId || 'da-DK', t })

  const report = [...reportHeader, [], ...reportData]

  if (withAuditTrail) {
    const auditTrailFlatData = buildFlatAuditTrailTable({ data: auditTrailData || [], locale: getCurrentLocale(), t })

    report.push([], [], [t('vat_declaration.audit_trail')], [], ...auditTrailFlatData)
  }

  const { periodSettled, year } = periodData || {}
  const period = periodSettled && year ? `-${periodSettled}-${year}` : ''

  const fileName = `${t('vat_declaration.export.file_name')}${period}`

  const { exportAsXlsx, isLoading: isXlsxLoading } = useXlsxExport({
    spreadSheetData: report,
    fileName,
    notificationKey: NotificationKeys.VatDeclarationExportXlsx,
  })

  const { exportAsCsv, isLoading: isCsvLoading } = useCsvExport({
    spreadSheetData: report,
    fileName,
    notificationKey: NotificationKeys.VatDeclarationExportCsv,
  })

  return { exportAsXlsx, exportAsCsv, isXlsxLoading, isCsvLoading }
}
